import { Navigate, Route, Routes } from 'react-router-dom';
import { LoginPage } from '@/features/auth/LoginPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { RequireAuth } from '@/features/auth/RequireAuth';
import { AppShell } from '@/features/layout/AppShell';
import { DashboardPage } from '@/pages/DashboardPage';
import { StudiesHubPage } from '@/features/knowledge/StudiesHubPage';
import { LegalLibraryPage } from '@/pages/LegalLibraryPage';
import { PublicEntitiesPage } from '@/pages/PublicEntitiesPage';
import { CircularsPage } from '@/pages/CircularsPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { CasesListPage } from '@/features/cases/CasesListPage';
import { CreateCasePage } from '@/features/cases/CreateCasePage';
import { CaseDetailPage } from '@/features/cases/CaseDetailPage';
import { StageDetailPage } from '@/features/cases/StageDetailPage';
import { ResolvedRegisterPage } from '@/features/resolvedregister/ResolvedRegisterPage';
import { ExecutionFilesPage } from '@/features/execution/ExecutionFilesPage';
import { ExecutionFileDetailPage } from '@/features/execution/ExecutionFileDetailPage';
import { NotificationsPage } from '@/features/notifications/NotificationsPage';
import { BroadcastPage } from '@/features/notifications/BroadcastPage';
import { LegalLibraryItemDetailPage } from '@/pages/LegalLibraryItemDetailPage';
import { PublicEntityDetailPage } from '@/pages/PublicEntityDetailPage';
import { CircularDetailPage } from '@/pages/CircularDetailPage';
import { AdminUsersListPage } from '@/features/admin-users/pages/AdminUsersListPage';
import { AdminUserDetailPage } from '@/features/admin-users/pages/AdminUserDetailPage';
import { ExternalDisputesPage } from '@/features/external-disputes/ExternalDisputesPage';
import { PendingSubmissionsPage } from '@/features/pending-submission/PendingSubmissionsPage';

/**
 * Phase 9 router.
 *
 * Public:
 *   /login
 *
 * Protected (any authenticated user — backend enforces fine-grained scope):
 *   /dashboard, /profile
 *   /cases, /cases/:caseId, /stages/:stageId
 *   /resolved-register
 *   /execution-files, /execution-files/:id
 *   /legal-library, /public-entities, /circulars
 *
 * NOT mounted yet (Phase 10+): attachments / reminders / notifications.
 */
export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* DEV-ONLY preview — lets you see AppShell watermark without backend. Remove later. */}
      <Route path="/preview-shell" element={<AppShell />}>
        <Route
          index
          element={
            <div className="p-8 text-slate-600">
              <h1 className="mb-2 text-lg font-semibold text-slate-800">معاينة التصميم</h1>
              <p className="text-sm">
                هذه صفحة معاينة مؤقتة — الشعار في الخلفية كعلامة مائية خفيفة.
              </p>
            </div>
          }
        />
      </Route>

      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard"             element={<DashboardPage />} />
          {/* Client feedback: profile is merged into the home page. Keep the
              old path working by redirecting it to the dashboard. */}
          <Route path="/profile"               element={<Navigate to="/dashboard" replace />} />

          {/* Phase 9 — business */}
          <Route path="/cases"                 element={<CasesListPage />} />
          <Route path="/cases/new"             element={<CreateCasePage />} />
          <Route path="/cases/:caseId"         element={<CaseDetailPage />} />
          <Route path="/stages/:stageId"       element={<StageDetailPage />} />
          <Route path="/resolved-register"     element={<ResolvedRegisterPage />} />
          {/* Phase 2 (#3) — تحت الرفع register. */}
          <Route path="/pending-submissions"   element={<PendingSubmissionsPage />} />
          <Route path="/execution-files"       element={<ExecutionFilesPage />} />
          <Route path="/execution-files/:id"   element={<ExecutionFileDetailPage />} />

          {/* Phase 10 — notifications (D-038) */}
          <Route path="/notifications"          element={<NotificationsPage />} />
          {/* PR-14 (customer feedback A-1 / Q-G expansion) — broadcast composer.
              Visual gate; backend re-validates per request. */}
          <Route path="/notifications/broadcast" element={<BroadcastPage />} />

          {/* Phase 7 reference (kept) — Phase 10 added detail pages.
              Client feedback: «قسم الدراسات» hub groups these. */}
          <Route path="/studies"                   element={<StudiesHubPage />} />
          {/* Phase 2 (#5b) — المنازعات الخارجية register, reached via the
              قسم الدراسات hub card. */}
          <Route path="/external-disputes"          element={<ExternalDisputesPage />} />
          <Route path="/legal-library"             element={<LegalLibraryPage />} />
          <Route path="/legal-library/items/:id"   element={<LegalLibraryItemDetailPage />} />
          <Route path="/public-entities"           element={<PublicEntitiesPage />} />
          <Route path="/public-entities/:id"       element={<PublicEntityDetailPage />} />
          <Route path="/circulars"                 element={<CircularsPage />} />
          <Route path="/circulars/:id"             element={<CircularDetailPage />} />

          {/* UI sub-phase B — `/admin/users` minimal (D-047 / D-048).
              CENTRAL_SUPERVISOR only — backend re-validates per request. */}
          <Route element={<RequireAuth anyOf={['CENTRAL_SUPERVISOR']} />}>
            <Route path="/admin/users"     element={<AdminUsersListPage />} />
            <Route path="/admin/users/:id" element={<AdminUserDetailPage />} />
          </Route>

          <Route path="*"                element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
